import { useEffect, useRef, useState } from "react";
import { ChevronsUpDown, Package } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";

export default function ItemMasterPicker({ onPick, testId }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [im, inv] = await Promise.all([
          api.get("/item-masters"),
          api.get("/inventory", { params: { stock: "Available" } }),
        ]);
        const masters = (im.data || []).map((r) => ({
          kind: "master", id: r.id, label: r.item_name, hsn: r.hsn || "",
          description: r.description || "", rate: r.rate ?? "", stock: Number(r.quantity || 0),
          incl: !!r.gst_inclusive,
        }));
        const units = (inv.data || []).map((r) => ({
          kind: "inventory", id: r.id, label: `${r.model_name} · ${r.asset_id}`,
          hsn: "", description: [r.configuration, r.serial_number].filter(Boolean).join(" · "),
          rate: "", stock: 1, incl: true,
        }));
        setRows([...masters, ...units]);
      } catch (e) { console.error("picker fetch fail", e); }
    })();
  }, []);

  useEffect(() => {
    function onDoc(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setOpen(false); setSearch(""); }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 0); }, [open]);

  const s = search.toLowerCase();
  const filtered = rows.filter((r) =>
    r.label.toLowerCase().includes(s) || (r.hsn || "").toLowerCase().includes(s) || (r.description || "").toLowerCase().includes(s));

  const pick = (r) => { onPick(r); setOpen(false); setSearch(""); };

  return (
    <div ref={wrapperRef} className="relative shrink-0">
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen((o) => !o)} className="h-8 gap-1.5 text-xs" data-testid={testId}>
        <Package className="w-3.5 h-3.5" /> Pick item <ChevronsUpDown className="w-3 h-3 opacity-50" />
      </Button>
      {open && (
        <div className="absolute z-[60] top-full right-0 mt-1 w-[340px] bg-white border border-[#E2E8F0] rounded-md shadow-lg overflow-hidden">
          <div className="p-2 border-b border-[#E2E8F0]">
            <input ref={inputRef} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search inventory & saved items…"
              className="w-full h-8 px-2 text-sm border border-[#E2E8F0] rounded outline-none focus:border-[#0055FF]" data-testid={`${testId}-input`} />
          </div>
          <div className="max-h-80 overflow-auto py-1">
            {filtered.length === 0 && <div className="px-3 py-2 text-sm text-muted-foreground">No matches. Add items in Item Masters or Inventory.</div>}
            {filtered.map((r) => (
              <button key={`${r.kind}-${r.id}`} type="button" className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50" data-testid={`${testId}-opt`}
                onClick={() => pick(r)}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium truncate">{r.label}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className={`text-[9px] font-mono uppercase px-1 py-0.5 rounded ${r.kind === "inventory" ? "bg-[#EEF2FF] text-[#4338CA]" : "bg-[#ECFDF5] text-[#047857]"}`}>{r.kind === "inventory" ? "STOCK" : "SAVED"}</span>
                    {r.rate !== "" && <span className="font-mono text-xs text-[#0055FF]">{`\u20B9${Number(r.rate || 0).toLocaleString("en-IN")}`}</span>}
                  </span>
                </div>
                <div className="text-[11px] truncate">
                  {r.kind === "master" && (
                    <span className={Number(r.stock) <= 0 ? "text-red-600 font-medium" : Number(r.stock) <= 5 ? "text-amber-600 font-medium" : "text-[#64748B]"}>
                      {r.hsn ? `HSN ${r.hsn} · ` : ""}Stock: {Number(r.stock)}{Number(r.stock) <= 0 ? " (out of stock)" : Number(r.stock) <= 5 ? " (low)" : ""}
                    </span>
                  )}
                  <span className="text-[#64748B]">{r.description ? `${r.kind === "master" && r.hsn ? " · " : ""}${r.description}` : ""}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
